import { ComplexTable } from '@synergy/table';
import { ConfigProvider, Divider, Form, Modal } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import query from '../../../../../../../server/constants.api';
import useFetch from '../../../../../../../server/useFetch';
import { showNotification } from '../../../../../../../utils/config/notificationHandler';
import permissionToEdit from '../../../AccessUtils';
import { handleNotificationForRelation } from '../../ItemsConfig/ItemsConfig';
import ItemCreateRelationForm from './ItemCreateRelationForm/ItemCreateRelationForm';

const ItemRelationsModal = ({ isItemRelationsModalOpen, handleItemRelationsClose, itemRelationsData, isLoading, itemSelectedName, refetchData }) => {
    const { t } = useTranslation();

    const tableConfiguration = {
        collumnsConfiguration: [
            {
                title: t('Relation'),
                dataIndex: 'relation',
            },
            {
                title: t('Parent Item'),
                dataIndex: 'parent',
            },
            {
                title: t('Child Item'),
                dataIndex: 'child',
            },
            {
                title: t('Weight'),
                dataIndex: 'weight',
            },
            {
                title: t('Is Peer'),
                dataIndex: 'isPeer',
            },
        ]
    };

    const [fieldToDisable, setFieldToDisable] = useState({ parent: true, child: false });
    const [relationToCreate, setRelationToCreate] = useState(null);
    const [selectedRelationToDelete, setSelectedRelationToDelete] = useState(null);
    const [isPeer, setIsPeer] = useState(false);

    const [form] = Form.useForm();
    const allowedToWrite = permissionToEdit(['Configurations', 'ItemsConfigurations'])
    const tenant = JSON.parse(localStorage.getItem('unified_nova_tenant'));

    //-------------------- Fetching SubTenants --------------------
    const {
        data: subTenants,
        refetch: refetchSubTenants,
        isFetching: isFetchingSubTenants
    } = useFetch('/items/distinct-properties',
        'getSubTenantsForRelation',
        {
            tenant: tenant[0],
            properties: "tenant",
        },
        true,
        null,
        query.middlewareURL
    )

    const {
        data: createRelationData,
        isFetching: isFetchingCreateRelation,
        refetch: refetchCreateRelation,
        isSuccess: isCreateRelationSuccess,
        isError: isCreateRelationError,
        error: createRelationError,
        remove: removeCreateRelation
    } = useFetch(
        '/items/relationships',
        'createRelation',
        relationToCreate,
        false,
        'POST',
        query.middlewareURL);

    const {
        data: deleteRelationData,
        isFetching: isFetchingDeleteRelation,
        refetch: refetchDeleteRelation,
        remove: removeDeleteRelation,
        isSuccess: isDeleteRelationSuccess,
        isError: isDeleteRelationError,
        error: deleteRelationError
    } = useFetch(
        `/items/relationships/id/${selectedRelationToDelete?.id}`,
        'deleteRelation',
        null,
        false,
        'DELETE',
        query.middlewareURL);

    const {
        data: itemsData,
        isFetching: isFetchingItemsData,
        refetch: refetchItemsData,
        remove: removeItemsData
    } = useFetch('/items',
        'getItemsByTenantForRelation',
        {
            tenant: tenant[0]
        },
        false,
        null,
        query.middlewareURL);


    const recordsToShowInForm = {
        "Parent": {
            key: 'parent',
            name: t('Parent Item'),
            fieldTypeOf: 'TextBox',
            disabled: fieldToDisable.parent,
            required: true
        },
        "Child": {
            key: 'child',
            name: t('Child Item'),
            fieldTypeOf: 'TextBox',
            disabled: fieldToDisable.child,
            required: true
        },
        "Weight": {
            key: 'weight',
            name: t('Weight'),
            fieldTypeOf: 'Number',
            rule: "^(100|[1-9]?[0-9])$",
            required: true
        },
        "IsPeer": {
            key: 'isPeer',
            name: t('Is Peer'),
            fieldTypeOf: 'Switch',
            required: false
        },
    }

    /**
     * Resets the form fields when the modal is open or closed
     */
    useEffect(() => {
        if (isItemRelationsModalOpen) {
            refetchItemsData();

            form.setFieldsValue({
                parent: itemSelectedName,
                child: '',
                weight: '',
                isPeer: false
            });

        } else {

            removeItemsData();
        }
    }, [isItemRelationsModalOpen]);

    /**
     * Make a fetch to create the relation
     */
    useEffect(() => {
        if (relationToCreate && !isFetchingCreateRelation) {
            refetchCreateRelation();
        }
    }, [relationToCreate]);

    useEffect(() => {
        if (selectedRelationToDelete && !isFetchingDeleteRelation) {
            refetchDeleteRelation();
        }
    }, [selectedRelationToDelete]);

    /**
     * Responsible for showing a notification when a relation is created
     * And remove the result from the create relation fetch
     */
    useEffect(() => {
        handleRelationEffect(isFetchingCreateRelation, isCreateRelationSuccess, isCreateRelationError,
            createRelationError, refetchData, removeCreateRelation, setRelationToCreate, t, true);
    }, [isCreateRelationSuccess, isCreateRelationError, isFetchingCreateRelation]);

    useEffect(() => {
        handleRelationEffect(isFetchingDeleteRelation, isDeleteRelationSuccess, isDeleteRelationError,
            deleteRelationError, refetchData, removeDeleteRelation, setSelectedRelationToDelete, t, false);
    }, [isDeleteRelationSuccess, isDeleteRelationError, isFetchingDeleteRelation]);

    useEffect(() => {
        if (!isItemRelationsModalOpen) return;
        if (isPeer) {
            form.setFieldsValue({
                weight: 0
            });
        } else {
            form.setFieldsValue({
                weight: ''
            });
        }
    }, [isPeer]);

    /**
     * Used to clean the data when the modal is unmounted
     * To not showing the notification when returning to items page
     */
    useEffect(() => {
        return () => {
            removeCreateRelation();
            removeDeleteRelation();
        }
    }, []);

    /**
     * Responsible for showing a notification when a relation is created
     * @param {*} isFetching 
     * @param {*} isSuccess 
     * @param {*} isError 
     * @param {*} error 
     * @param {*} refetchData 
     * @param {*} removeFetch 
     * @param {*} setRelation 
     * @param {*} t 
     */
    const handleRelationEffect = (isFetching, isSuccess, isError, error, refetchData, removeFetch, setRelation, t, isToCreate) => {
        handleNotificationForRelation(isFetching, isSuccess, isError, error, t, isToCreate);
        if (isSuccess && refetchData) {
            refetchData();
            resetForm();
        }

        if (isSuccess || isError) {
            removeFetch();
            setRelation(null);
        }
    }

    /**
     * Reset the form fields
     */
    const resetForm = () => {
        form.setFieldsValue({
            parent: itemSelectedName,
            child: '',
            weight: '',
            isPeer: false
        });
        setFieldToDisable({ parent: true, child: false });
        setIsPeer(false);
        setRelationToCreate(null);
        setSelectedRelationToDelete(null);
    }

    /**
     * Searches for the item in the itemsData
     * @param {*} name The name of the item to be searched
     * @returns The item found
     */
    const searchInItemsData = (name) => {
        if (itemsData) {
            return itemsData.find(item => item.name === name);
        }
    }

    /**
    * Switches the values of the Parent and Child fields
    */
    const handleSwitch = () => {

        setFieldToDisable({
            parent: !fieldToDisable.parent,
            child: !fieldToDisable.child
        });

        form.setFieldsValue({
            parent: form.getFieldValue('child'),
            child: form.getFieldValue('parent')
        });
    }

    /**
     * Called when the button to create a relation is clicked
     * @param {*} relation relation to be created
     */
    const handleCreateRelation = (relation) => {
        const nameFieldInsertedByUser = Object.keys(fieldToDisable).find(key => fieldToDisable[key] === false);

        const itemByUser = searchInItemsData(relation[nameFieldInsertedByUser]);

        if (itemByUser) {

            const relationToCreate = serializeRelation(relation);

            setRelationToCreate(relationToCreate);
        } else {
            showNotification('error', 'Error', t(`The item does not exist in the ${nameFieldInsertedByUser} field`));
        }
    }

    /**
     * Serializes the relation to be created
     * @param {*} relation The relation to be serialized
     * @returns The serialized relation
     */
    const serializeRelation = (relation) => {
        const parentItem = searchInItemsData(relation.parent);
        const childItem = searchInItemsData(relation.child);

        return {
            parent: parentItem.id,
            child: childItem.id,
            weight: isPeer ? '0' : relation.weight,
            is_peer: relation.isPeer,
            tenant: childItem.tenant
        }
    }

    return (
        <Modal
            title={t("Relations of") + " " + itemSelectedName}
            open={isItemRelationsModalOpen}
            width="70rem"
            onCancel={handleItemRelationsClose}
            footer={null}
            afterClose={resetForm}
        >
            <ConfigProvider
                theme={{
                    components: {
                        Form: {
                            margin: 0
                        }
                    }
                }}
            >
                <Form
                    name="CreateRelationForm"
                    layout="inline"
                    style={{ marginTop: '40px' }}
                    form={form}
                    onFinish={(values) => {
                        handleCreateRelation(values);
                    }}
                    onValuesChange={(changedValues) => {
                        if (changedValues.hasOwnProperty('isPeer')) {
                            setIsPeer(changedValues.isPeer);
                        }
                    }}
                >
                    <ItemCreateRelationForm
                        form={form}
                        handleSwitch={handleSwitch}
                        recordsToShowInForm={recordsToShowInForm}
                        showWeight={!isPeer}
                        isLoading={isFetchingCreateRelation}
                    />
                </Form >
            </ConfigProvider>
            <Divider />
            <ComplexTable
                isLoading={isLoading}
                fetchData={itemRelationsData}
                tableConfiguration={tableConfiguration}
                language={i18next.resolvedLanguage}
                tableId='itemRelationsTable'
                pagination={{ pageSize: 5 }}
                fixedActions={true}
                deleteButton={allowedToWrite}
                handleDelete={(item) => {
                    setSelectedRelationToDelete(item);
                }}
            />
        </Modal >
    );
}

export default ItemRelationsModal;